import { styled } from '@mui/material';
import type { BoxProps, CardProps, CollapseProps, FlatIconButtonProps } from '@packages/shared';
import {
  FlatIconButton,
  Box,
  Card,
  CardActions,
  Collapse,
  Divider,
  Typography,
} from '@packages/shared';
import { ArrowMediumDown32 } from '@packages/themes/icons';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';

type ExpandMoreIconProps = FlatIconButtonProps & {
  expanded: boolean;
};

const ExpandMoreIcon = styled((props: ExpandMoreIconProps) => {
  const { expanded, ...other } = props;
  return (
    <FlatIconButton {...other} sx={{ p: 0 }}>
      <ArrowMediumDown32 />
    </FlatIconButton>
  );
})(({ theme, expanded }) => ({
  transform: !expanded ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

/**
 * Styled Box, which functions as overlay with an opaque background.
 */
export const Overlay = styled((props: { expanded: boolean } & BoxProps) => {
  const { expanded, ...other } = props;
  return (
    <Box
      {...other}
      sx={{
        height: '4rem',
        position: 'absolute',
        background:
          'linear-gradient(to top, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%)',
        left: 0,
        bottom: 0,
        right: 0,
        willChange: 'opacity',
        opacity: expanded ? 0 : 1,
      }}
    />
  );
})(({ theme }) => ({
  transition: theme.transitions.create('opacity', {
    duration: theme.transitions.duration.short,
  }),
}));

export type CollapsibleCardProps = {
  /** Optional children react node */
  children?: ReactNode;
  /**
   * Children for the collapsed content.
   */
  collapseChildren: ReactNode;

  /**
   * The width (horizontal) or height (vertical) of the container when collapsed.
   * @default '0px'
   */
  collapsedSize?: CollapseProps['collapsedSize'];
  /**
   * Text that can be clicked to extend/collapse the card.
   */
  cardActionText: {
    /**
     * Text that should be shown to extend card
     */
    expandText: string;
    /**
     * Text that should be shown to collapse card
     */
    collapseText: string;
  };
} & CardProps;

/**
 * This Component is an extension of the Card component from MUI: https://mui.com/material-ui/react-card/
 *
 * In addition to the default Card, you can add collapseChildren, which can be expanded on click.
 * */
export const CollapsibleCard: FC<CollapsibleCardProps> = ({
  cardActionText,
  children,
  collapseChildren,
  collapsedSize,
  ...rest
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <Card {...rest}>
      {children}
      <Collapse collapsedSize={collapsedSize} in={expanded} sx={{ position: 'relative' }}>
        {!collapsedSize && <Divider />}
        {collapsedSize && <Overlay expanded={expanded} />}
        {collapseChildren}
      </Collapse>
      <Divider />
      <CardActions onClick={() => handleExpandClick()} sx={{ cursor: 'pointer', gap: 1 }}>
        <Typography variant="body2">
          {/** If card is expanded then it shows the text to collapse the card and vice versa */}
          {expanded ? cardActionText.collapseText : cardActionText.expandText}
        </Typography>
        <ExpandMoreIcon expanded={expanded} />
      </CardActions>
    </Card>
  );
};
